import { useEffect, useState } from 'react';
import { config } from '../config/environment';

export interface StateOption {
  /** Subdivision code (e.g. "CA" for California, "ON" for Ontario). */
  code: string;
  /** Human-readable name (e.g. "California", "Ontario"). */
  name: string;
  /** Advisory subdivision type: "state" | "province" | "territory" | "district". */
  type: string;
}

/**
 * Single source of state/province reference data for every address form
 * (E1.3 / CSECR-16, wired everywhere in E1.8 / CSECR-64).
 *
 * Loads the active subdivisions for a country ISO-3166-1 alpha-2 code from the
 * DB-backed endpoint `GET /api/countries/{iso}/states`. The endpoint always
 * returns a 200 with a (possibly empty) list, so this hook returns an empty
 * list for a blank/unknown country, when the country has no seeded rows, or on
 * any network error. Callers treat an empty list as "render a free-text
 * state/province input" (graceful degradation; the field stays required).
 *
 * Do NOT reintroduce a hardcoded states map in any component — use this hook so
 * the list stays admin-manageable in the `state_provinces` table.
 */
export function useCountryStates(country: string): { states: StateOption[]; loading: boolean } {
  const [states, setStates] = useState<StateOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const iso = (country || '').trim();
    if (!iso) {
      setStates([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${config.apiBaseUrl}/countries/${encodeURIComponent(iso)}/states`);
        if (!res.ok) {
          if (!cancelled) setStates([]);
          return;
        }
        const data: StateOption[] = await res.json();
        if (!cancelled) setStates(Array.isArray(data) ? data : []);
      } catch {
        // Non-fatal: fall back to a free-text state/province input.
        if (!cancelled) setStates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [country]);

  return { states, loading };
}

export default useCountryStates;
